// @flow

import React from 'react';
import discoveredFeed from 'app/common/redux/actions/discoveredFeed/discoveredFeed';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import urlUtil from 'app/common/lib/urlUtil';
import withViewer from 'app/common/lib/react/withViewer';
import { ActivitiesPinsGrid } from 'app/common/react/components/profile/UserProfilePinGrid/UserProfilePinGrid';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import { Box, Button, Container, Text } from 'gestalt';

export type Props = {
  dispatch: Function,
  firstName: string,
  isOwnProfile: boolean,
  params: {
    username: string,
  },
  user: {
    domain_url: string,
    domain_verified: boolean,
    first_name: string,
    id: string,
    username: string,
  },
  viewer: AuthViewer,
};

export class ProfileActivities extends React.Component<Props, void> {
  toggleShowDiscoveredFeed = () => {
    const { dispatch, viewer } = this.props;
    const options = {
      profile_discovered_public: !viewer.profileDiscoveredPublic,
    };
    const resource = ResourceFactory.create('UserMetadataResource', options);
    resource.callUpdate();
    dispatch(discoveredFeed(options.profile_discovered_public));
    const eventType = options.profile_discovered_public
      ? PCONST.thrift.EventType.DISCOVERED_FEED_SELECT_PUBLIC
      : PCONST.thrift.EventType.DISCOVERED_FEED_SELECT_PRIVATE;
    track(eventType, {
      view: PCONST.thrift.ViewType.USER,
      viewParameter: PCONST.thrift.ViewParameterType.USER_SELF,
    });
  };

  renderActivityText = () => {
    const {
      user: { domain_url },
    } = this.props;

    if (!domain_url || !domain_url.trim()) {
      return null;
    }

    return (
      <Box justifyContent="center" display="flex">
        <Text align="center">
          {interpolateNamedTemplate(
            i18n._(
              'Pins people saved from {{ claimed_website }}',
              'Explanatory text for the source of the pins under the Activity tab',
            ),
            {
              claimed_website: (
                <NavigateLink
                  key="userClaimedWebsite"
                  to={urlUtil.absolutify(domain_url, undefined, '')}
                  external
                >
                  {domain_url}
                </NavigateLink>
              ),
            },
          )}
        </Text>
      </Box>
    );
  };

  renderActivityToggle = () => {
    const showDiscoveredFeed = this.props.viewer.profileDiscoveredPublic;
    let text = '';
    let buttonColor = '';
    let buttonText = '';

    if (showDiscoveredFeed) {
      text = i18n._(
        'These are all the Pins people saved from your claimed website and linked accounts. ' +
          'This page is visible on your profile right now, but you can always remove it.',
        'Label for toggling whether the activities tab on creator profile is visible',
      );
      buttonColor = 'gray';
      buttonText = i18n._(
        'Remove from profile',
        'toggle to make activites tab private on creator profile',
      );
    } else {
      text = i18n._(
        'These are all the Pins people saved from your claimed website and linked accounts. ' +
          'Only you can see this page right now.',
        'Label for toggling whether the activities tab on creator profile is visible',
      );
      buttonColor = 'red';
      buttonText = i18n._(
        'Add to profile',
        'toggle to make activites tab public on creator profile',
      );
    }
    const button = (
      <Button color={buttonColor} text={buttonText} onClick={this.toggleShowDiscoveredFeed} />
    );
    return (
      <Container>
        <Box alignItems="center" column={12} display="flex" justifyContent="between" paddingY={4}>
          <Box column={6} marginRight={4}>
            <Text size="lg">{text}</Text>
          </Box>
          <Box flex="shrink">{button}</Box>
        </Box>
      </Container>
    );
  };

  render() {
    const { isOwnProfile, user } = this.props;

    const { username } = user;

    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;

    const activityToggle = isOwnProfile ? this.renderActivityToggle() : this.renderActivityText();

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={timeSpentViewParam}
        component={PCONST.thrift.ComponentType.USER_DISCOVERED}
      >
        <Box>
          {activityToggle}
          <ActivitiesPinsGrid isOwnProfile={isOwnProfile} user={user} username={username} />
        </Box>
      </TimeSpent>
    );
  }
}

export default compose(
  withViewer,
  withRouter,
)(ProfileActivities);
